import { useState } from 'react';
import { when } from '../../lib/n4';
import { type Site, type TestState, errText, flag, noteOf, post, siteApi, toMs } from './common';

// Teszt-példány fül: a konténer-recept másolata -test névvel, szabad porton, az adat és a DB másolatával.
export function TestInstance({ site, onChanged }: { site: Site; onChanged: () => void }) {
  const known: TestState | null = site.test ?? site.live?.test ?? null;
  const [local, setLocal] = useState<TestState | null>(null);
  const [busy, setBusy] = useState<'start' | 'drop' | null>(null);
  const [err, setErr] = useState('');
  const [expose, setExpose] = useState(flag(site.test_expose));
  const [startNote, setStartNote] = useState('');
  const t = local ?? known;
  // A DNS-rekord állapota: { ok } vagy { ok:false, note } (pl. nincs Cloudflare-token → mit kell kézzel)
  const dns = t?.dns ?? null;
  const running = Boolean(t && (t.running ?? t.port));
  const url = t?.url ?? (t?.port ? `${location.protocol}//${location.hostname}:${t.port}` : null);

  const start = async () => {
    setBusy('start'); setErr('');
    try {
      const r = await siteApi<TestState & { test?: TestState }>(site.id, '/test', post(expose ? { expose: true } : {}));
      const x = r.test ?? r;
      setLocal({ running: true, ...x, public_url: x.public_url ?? r.public_url ?? null, dns: x.dns ?? r.dns ?? null }); setStartNote(noteOf(r)); onChanged();
    }
    catch (e) { setErr(errText(e, 'Teszt-példány indítása')); } finally { setBusy(null); }
  };
  const drop = async () => {
    if (!confirm(`Leállítod és törlöd a teszt-példányt (n4site-${site.name}-test) az adat-másolatával együtt?`)) return;
    setBusy('drop'); setErr('');
    try { await siteApi(site.id, '/test-drop', post()); setLocal({ running: false, port: null, url: null, public_url: null, dns: null }); setStartNote(''); onChanged(); }
    catch (e) { setErr(errText(e, 'Teszt-példány törlése')); } finally { setBusy(null); }
  };

  return (
    <div className="a-site-test">
      <div className={`a-panel a-pad a-site-teststate ${running ? 'run' : ''}`}>
        <div className="a-site-top"><i className={`a-sitelamp ${running ? 'run' : 'stop'}`} /><b>{running ? 'Teszt-példány fut' : 'Nincs teszt-példány'}</b></div>
        {running ? (
          <p className="a-site-note">Konténer: <code>n4site-{site.name}-test</code>{t?.port ? <> · port <code>{t.port}</code></> : null}{t?.started_at ? <> · indult {when(toMs(t.started_at))}</> : null}<br />
            {url && <a href={url} target="_blank" rel="noreferrer" className="a-site-link">{url} ↗</a>}
            {t?.public_url && <><br />Kint az edge-en: <a href={t.public_url} target="_blank" rel="noreferrer" className="a-site-link">{t.public_url} ↗</a></>}</p>
        ) : (
          <p className="a-site-note">A teszt-példány az éles recept másolata, saját porton, az adatok és az adatbázis MÁSOLATÁVAL — kipróbálhatod rajta a változást anélkül, hogy az éles oldal adatához nyúlnál.{site.kind !== 'container' ? ' (Csak konténer fajtájú oldalnál érhető el.)' : ''}</p>
        )}
        {running && dns && (dns.ok === false || dns.note) && <div className={`a-site-msg ${dns.ok === false ? 'bad' : ''}`}><b>DNS:</b> {dns.note || (dns.ok === false ? 'a rekord nem jött létre' : '')}</div>}
        {running && startNote && <div className="a-site-msg">{startNote}</div>}
        <div className="flex flex-wrap items-center gap-2">
          {!running && <label className="a-site-check-inline big" title="A teszt-példány saját aldomaint kap (<név>-test.naryan.net) certtel — telefonról, másik gépről is kipróbálható"><input type="checkbox" checked={expose} disabled={busy != null || site.kind !== 'container'} onChange={(e) => setExpose(e.target.checked)} />Kint az edge-en (saját aldomain + cert)</label>}
          {!running && <button className="a-btn sm solid" disabled={busy != null || site.kind !== 'container'} onClick={() => void start()}>{busy === 'start' ? 'Indítás…' : 'Teszt-példány indítása'}</button>}
          {running && <button className="a-btn sm" disabled={busy != null} onClick={() => void drop()}>{busy === 'drop' ? 'Törlés…' : 'Leállítás és törlés'}</button>}        </div>
      </div>
      {err && <div className="a-site-msg bad">{err}</div>}
    </div>
  );
}
